import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  LocalTaxi as TaxiIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const About: React.FC = () => {
  const { token } = useAuth();
  const isLoggedIn = Boolean(token);

  const values = [
    { icon: <VerifiedIcon />, title: 'Authorized sources', body: 'Ride requirements are organized from authorized sources and verified contributors before they reach drivers.' },
    { icon: <TaxiIcon />,     title: 'Built for drivers',  body: 'We designed every screen with taxi operators in mind, not tech teams.' },
    { icon: <GroupsIcon />,   title: 'Community first',    body: 'RideSaathi grows when drivers share and refer. Our referral program reflects that.' },
    { icon: <TrophyIcon />,   title: 'Driver success',     body: 'We measure our success by how many rides our partners successfully close each day.' },
  ];

  const team = [
    { name: 'Sukhwant Chahal', role: 'Founder · Chahal Travel', avatar: 'SC' },
    { name: 'Product Team',    role: 'Engineering & Design',     avatar: 'PT' },
    { name: 'Driver Feedback', role: 'Product decisions guided by active taxi partners', avatar: 'DF' },
  ];

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg,#061b31,#0b3156)', py: { xs: 10, md: 14 }, px: 3 }}>
        <Stack alignItems="center" spacing={2.5} textAlign="center">
          <Chip label="OUR STORY" sx={{ bgcolor: 'rgba(255,201,40,0.12)', color: '#ffc928', fontWeight: 700, letterSpacing: 1 }} />
          <Typography variant="h2" fontWeight={800} color="white" sx={{ fontSize: { xs: '2rem', md: '3rem' }, maxWidth: 640 }}>
            We're building the taxi lead platform India deserves
          </Typography>
          <Typography variant="h6" color="rgba(226,232,240,0.7)" maxWidth={520} fontWeight={400} lineHeight={1.7}>
            RideSaathi started as an internal tool for Chahal Travel - a way to turn ride requirements from trusted contributors into a structured lead feed. We opened it to every driver who needed the same edge.
          </Typography>
        </Stack>
      </Box>

      {/* Mission */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Grid container spacing={6} alignItems="center" sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 3, md: 6 } }}>
          <Grid item xs={12} md={6}>
            <Stack spacing={2.5}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1.5}>OUR MISSION</Typography>
              <Typography variant="h3" fontWeight={800} sx={{ fontSize: { xs: '1.9rem', md: '2.4rem' }, lineHeight: 1.2 }}>
                Make every taxi driver's day predictable and profitable
              </Typography>
              <Typography variant="body1" color="text.secondary" lineHeight={1.8}>
                Drivers should not have to sort through scattered ride requirements guessing which requests are useful. We built RideSaathi to turn authorized and contributor-submitted rides into a structured feed so you can focus on driving.
              </Typography>
              <Typography variant="body1" color="text.secondary" lineHeight={1.8}>
                We're proudly made in India, priced in INR, and built specifically for the North India taxi corridor.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {[
                { v: 'Free', l: 'Monthly credits' },
                { v: '₹79',  l: 'Starter monthly plan' },
                { v: '₹149', l: 'Pro monthly plan' },
                { v: 'Live', l: 'Authorized ride lead feed' },
              ].map((s) => (
                <Grid item xs={6} key={s.l}>
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center', border: '1px solid rgba(15,23,42,0.07)' }}>
                    <Typography variant="h3" fontWeight={800} color="#07182d">{s.v}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.l}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Values */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Stack alignItems="center" spacing={1.5} mb={7}>
          <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1.5}>WHAT WE STAND FOR</Typography>
          <Typography variant="h3" fontWeight={800} textAlign="center" sx={{ fontSize: { xs: '1.9rem', md: '2.3rem' } }}>Our values</Typography>
        </Stack>
        <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 3, md: 6 } }}>
          {values.map((v) => (
            <Grid item xs={12} sm={6} key={v.title}>
              <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 3, height: '100%', border: '1px solid rgba(15,23,42,0.07)' }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: '#fff4c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9a6a00', mb: 2 }}>
                  {v.icon}
                </Box>
                <Typography variant="subtitle1" fontWeight={700} mb={0.75}>{v.title}</Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{v.body}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Team */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Stack alignItems="center" spacing={1.5} mb={7}>
          <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1.5}>THE TEAM</Typography>
          <Typography variant="h3" fontWeight={800} textAlign="center" sx={{ fontSize: { xs: '1.9rem', md: '2.3rem' } }}>Behind RideSaathi</Typography>
        </Stack>
        <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 800, mx: 'auto', px: { xs: 3, md: 6 } }}>
          {team.map((m) => (
            <Grid item xs={12} sm={4} key={m.name}>
              <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 3, textAlign: 'center', border: '1px solid rgba(15,23,42,0.07)' }}>
                <Avatar sx={{ bgcolor: '#ffc928', color: '#07182d', fontWeight: 800, width: 56, height: 56, mx: 'auto', mb: 1.5, fontSize: 16 }}>
                  {m.avatar}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700}>{m.name}</Typography>
                <Typography variant="caption" color="text.secondary">{m.role}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 8, md: 10 }, background: 'linear-gradient(135deg,#061b31,#0b3156)', textAlign: 'center', px: 3 }}>
        <Stack alignItems="center" spacing={2.5}>
          <Typography variant="h4" fontWeight={800} color="white">Ready to join the platform?</Typography>
          <Typography variant="body1" color="rgba(226,232,240,0.7)" maxWidth={420}>Start with free unlocks every month. No card required.</Typography>
          <Button component={RouterLink} to={isLoggedIn ? '/rides' : '/signup'} variant="contained" color="secondary" size="large" sx={{ fontWeight: 700, px: 4 }}>
            {isLoggedIn ? 'Browse live rides' : 'Create free account'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default About;
